import clsx from "clsx";
import { AnimatePresence, motion } from "motion/react";
import { FC } from "react";
import { useTranslation } from "react-i18next";

import { CollapseIcon } from "@ryde/controls";
import {
  IRydeProcess,
  onActivateKeyDown,
  ProcessStatusEnum,
} from "@ryde/shared";

import { expandVariants } from "./grid.models";
import { ProcessesGridRowDetails } from "./processes-grid-row-details";
import { processesHeaders } from "./processes-grid.models";
import { useProcessesHistoryContext } from "./processes-history-wrapper";

interface IProcessesGridRowProps {
  process: IRydeProcess;
  isExpanded?: boolean;
  onToggleExpanded?: (id: number) => void;
}

const getProcessValue = (process: IRydeProcess, key: string): string => {
  switch (key) {
    case "id":
      return String(process.id);
    case "title":
      return String(process.title || "");
    case "records":
      return process.affectedIds && process.affectedIds.length > 0
        ? String(process.affectedIds.length)
        : "0";
    case "status":
      return String(process.status || "");
    case "userName":
      if (process.user?.firstName && process.user?.lastName) {
        return `${String(process.user.firstName)} ${String(process.user.lastName)}`;
      } else if (process.user?.firstName || process.user?.lastName) {
        return String(process.user?.firstName || process.user?.lastName || "");
      }
      return String(process.userId || "");
    case "createdAt":
      return process.createdAt
        ? new Date(process.createdAt).toLocaleString()
        : "";
    default:
      return "";
  }
};

export const ProcessesGridRow: FC<IProcessesGridRowProps> = ({
  process,
  isExpanded: externalIsExpanded,
  onToggleExpanded,
}) => {
  const { expandedItems, onToggleExpanded: contextToggleExpanded } =
    useProcessesHistoryContext();
  const { t } = useTranslation();

  const isExpanded =
    externalIsExpanded !== undefined
      ? externalIsExpanded
      : expandedItems.has(process.id);

  const toggleExpanded = (): void => {
    if (onToggleExpanded) {
      onToggleExpanded(process.id);
    } else {
      contextToggleExpanded(process.id);
    }
  };

  const statusColors: Record<ProcessStatusEnum, string> = {
    [ProcessStatusEnum.Pending]: "processes-history__status--pending",
    [ProcessStatusEnum.Completed]: "processes-history__status--completed",
    [ProcessStatusEnum.Failed]: "processes-history__status--failed",
  };

  const statusColorClass = statusColors[process.status] || "";

  return (
    <div>
      <div
        className={clsx("processes-history__grid-row", {
          [`processes-history__grid-row--expanded`]: isExpanded,
        })}
        role="button"
        tabIndex={0}
        onClick={toggleExpanded}
        onKeyDown={onActivateKeyDown(toggleExpanded)}
      >
        <div className="processes-history__grid-cell processes-history__grid-cell_expanded">
          <CollapseIcon isCollapsed={isExpanded} variant="quaternary" />
        </div>
        {Object.keys(processesHeaders).map(key => {
          const value = getProcessValue(process, key);
          const isStatus = key === "status";

          return (
            <div
              key={key}
              className={clsx("processes-history__grid-cell", {
                [statusColorClass]: isStatus,
              })}
            >
              <span>
                {isStatus ? t(`process.status.${value}`) || value : value}
              </span>
            </div>
          );
        })}
      </div>

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            className="processes-history__grid-row-details"
            variants={expandVariants}
            initial="collapsed"
            animate="expanded"
            exit="collapsed"
          >
            <ProcessesGridRowDetails process={process} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
